// Splash overlay. Shown briefly on first visit per session — gives the
// app a calm, branded entry point during auth context resolution.
//
// Design rules:
// - Auto-dismiss after a minimum hold (default 1.6s) AND once auth has
//   settled, whichever is later. We never block the user past 2.5s in
//   the worst case.
// - Click-to-skip anywhere on the overlay.
// - Only on first session visit (sessionStorage flag) so refreshing
//   doesn't keep showing it.
// - Fades out; doesn't snap.
//
/* eslint-disable react-refresh/only-export-components -- splash co-locates
   its sessionStorage helpers with the component since they're conceptually
   one thing; not worth a second file. */

import { useEffect, useState } from 'react';
import { DictionaryEntry } from './DictionaryEntry';

const SESSION_KEY = 'rouser:splash-seen';
const MIN_DISPLAY_MS = 1600;
const MAX_DISPLAY_MS = 2500;

export function shouldShowSplash(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return !sessionStorage.getItem(SESSION_KEY);
  } catch {
    return false;
  }
}

export function markSplashSeen(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // Private browsing etc. — fail silent; worst case the splash shows
    // again next visit, which is harmless.
  }
}

export function Splash({
  onDismiss,
  authReady,
}: {
  onDismiss: () => void;
  /** True once the auth context has resolved. Used as the dismissal trigger. */
  authReady: boolean;
}) {
  const [fadingOut, setFadingOut] = useState(false);
  const [minHoldElapsed, setMinHoldElapsed] = useState(false);

  useEffect(() => {
    const minTimer = setTimeout(() => setMinHoldElapsed(true), MIN_DISPLAY_MS);
    const maxTimer = setTimeout(() => beginDismiss(), MAX_DISPLAY_MS);
    return () => {
      clearTimeout(minTimer);
      clearTimeout(maxTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (minHoldElapsed && authReady) beginDismiss();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minHoldElapsed, authReady]);

  function beginDismiss() {
    if (fadingOut) return;
    setFadingOut(true);
    markSplashSeen();
    setTimeout(onDismiss, 320);
  }

  return (
    <div
      onClick={beginDismiss}
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-6)',
        background: 'var(--color-bg)',
        zIndex: 'var(--z-overlay)',
        opacity: fadingOut ? 0 : 1,
        transition: 'opacity 320ms var(--ease)',
        cursor: 'pointer',
      }}
    >
      <DictionaryEntry size="lg" animatedLogo />
      <div
        style={{
          position: 'absolute',
          bottom: 'var(--space-8)',
          left: 0,
          right: 0,
          textAlign: 'center',
          color: 'var(--color-text-tertiary)',
          fontSize: 'var(--fs-xs)',
          letterSpacing: 'var(--tracking-wide)',
          textTransform: 'uppercase',
          opacity: minHoldElapsed ? 1 : 0,
          transition: 'opacity 400ms var(--ease)',
        }}
      >
        Tap anywhere to continue
      </div>
    </div>
  );
}
